import type { SalonSnapshot } from '../application/contracts.js';
import { SALON_SHOWCASE_VERSION, SHOWCASE_SCHEMA_VERSION } from '../application/contracts.js';
import type { SalonStatePort } from '../application/state-port.js';
import { createSalonSeed } from '../showcase/seed.js';

export const DB_NAME = 'calcomate-salon-local-v1';
export const STAGING_DB_NAME = 'calcomate-salon-restore-staging-v1';
export const DB_VERSION = 1;

export interface StateRecord { key: 'active'; revision: number; updatedAt: string; snapshot: SalonSnapshot; }
export interface PhotoRecord { id: string; clientPartyId: string; kind: 'before'|'after'|'document'; name: string; mimeType: string; size: number; note: string; createdAt: string; blob: Blob; }
export interface LocalMeta { key: string; value: unknown; }

const request = <T>(value: IDBRequest<T>): Promise<T> => new Promise((resolve, reject) => {
  value.onsuccess = () => resolve(value.result);
  value.onerror = () => reject(value.error ?? new Error('INDEXEDDB_REQUEST_FAILED'));
});
const transactionDone = (transaction: IDBTransaction): Promise<void> => new Promise((resolve, reject) => {
  transaction.oncomplete = () => resolve();
  transaction.onabort = () => reject(transaction.error ?? new Error('INDEXEDDB_TRANSACTION_ABORTED'));
  transaction.onerror = () => reject(transaction.error ?? new Error('INDEXEDDB_TRANSACTION_FAILED'));
});

export function openSalonDb(name = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const opening = indexedDB.open(name, DB_VERSION);
    opening.onupgradeneeded = () => {
      const db = opening.result;
      if (!db.objectStoreNames.contains('state')) db.createObjectStore('state', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('blobs')) {
        const blobs = db.createObjectStore('blobs', { keyPath: 'id' });
        blobs.createIndex('clientPartyId', 'clientPartyId', { unique: false });
      }
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('operations')) db.createObjectStore('operations', { keyPath: 'id' });
    };
    opening.onsuccess = () => resolve(opening.result);
    opening.onerror = () => reject(opening.error ?? new Error('INDEXEDDB_OPEN_FAILED'));
    opening.onblocked = () => reject(new Error('INDEXEDDB_UPGRADE_BLOCKED'));
  });
}

export function isBrowserSnapshot(value: unknown): value is SalonSnapshot {
  const candidate = value as Partial<SalonSnapshot> | null;
  return Boolean(candidate && candidate.schema === SHOWCASE_SCHEMA_VERSION && candidate.version === SALON_SHOWCASE_VERSION &&
    candidate.organization?.id && candidate.branch?.id && Array.isArray(candidate.staff) && Array.isArray(candidate.clients) &&
    Array.isArray(candidate.appointments) && Array.isArray(candidate.stockItems) && Array.isArray(candidate.financePayments));
}

export class IndexedDbSalonStore implements SalonStatePort {
  private db: IDBDatabase | null = null;
  private revision = 0;
  private writer = false;
  private releaseLock: (() => void) | null = null;
  private fallbackLease: number | null = null;
  readonly tabId = crypto.randomUUID();

  constructor(private readonly referenceDate = new Date(), private readonly report: (stage: string, details?: Record<string, unknown>) => void = () => undefined, private readonly seedWhenEmpty = true) {}

  async initialize(): Promise<void> {
    this.db = await openSalonDb();
    this.writer = await this.acquireWriterLock();
    this.report('BROWSER_STORE_OPEN', { writer: this.writer, schema: SHOWCASE_SCHEMA_VERSION });
    if (!await this.readRecord() && this.seedWhenEmpty) {
      if (!this.writer) throw new Error('READ_ONLY_TAB_WAITING_FOR_PRIMARY');
      await this.writeInitial(createSalonSeed(this.referenceDate));
    }
    addEventListener('pagehide', () => this.close(), { once: true });
  }

  get isWriter(): boolean { return this.writer; }

  async load(): Promise<SalonSnapshot> {
    const record = await this.readRecord();
    if (!record || !isBrowserSnapshot(record.snapshot)) throw new Error('LOCAL_DATABASE_INVALID');
    this.revision = record.revision;
    return structuredClone(record.snapshot);
  }

  async replace(snapshot: SalonSnapshot): Promise<SalonSnapshot> {
    if (!this.writer) throw new Error('READ_ONLY_TAB');
    if (!isBrowserSnapshot(snapshot)) throw new Error('LOCAL_DATABASE_INVALID');
    const db = this.requireDb(), transaction = db.transaction(['state', 'operations'], 'readwrite');
    const state = transaction.objectStore('state'), current = await request(state.get('active')) as StateRecord | undefined;
    if (!current || current.revision !== this.revision) { transaction.abort(); throw new Error('LOCAL_DATABASE_REVISION_CONFLICT'); }
    const next: StateRecord = { key: 'active', revision: current.revision + 1, updatedAt: new Date().toISOString(), snapshot: structuredClone(snapshot) };
    state.put(next);
    transaction.objectStore('operations').put({ id: `${next.revision}:${this.tabId}`, revision: next.revision, at: next.updatedAt, tabId: this.tabId });
    await transactionDone(transaction);
    this.revision = next.revision;
    return structuredClone(snapshot);
  }

  async reset(confirmed: boolean): Promise<SalonSnapshot> {
    if (!confirmed) throw new Error('SHOWCASE_RESET_CONFIRMATION_REQUIRED');
    if (!this.writer) throw new Error('READ_ONLY_TAB');
    const snapshot = createSalonSeed(this.referenceDate);
    const current = await this.readRecord(); this.revision = current?.revision ?? 0;
    return this.replace(snapshot);
  }

  async getRecord(): Promise<StateRecord> {
    const value = await this.readRecord();
    if (!value) throw new Error('LOCAL_DATABASE_EMPTY');
    return value;
  }

  async hasState(): Promise<boolean> { return Boolean(await this.readRecord()); }

  async initializeWith(snapshot: SalonSnapshot): Promise<void> {
    if (!this.writer) throw new Error('READ_ONLY_TAB');
    if (await this.readRecord()) throw new Error('LOCAL_DATABASE_ALREADY_INITIALIZED');
    if (!isBrowserSnapshot(snapshot)) throw new Error('LOCAL_DATABASE_INVALID');
    await this.writeInitial(snapshot);
  }

  async listPhotos(clientPartyId?: string): Promise<PhotoRecord[]> {
    const transaction = this.requireDb().transaction('blobs', 'readonly'), store = transaction.objectStore('blobs');
    const values = clientPartyId ? await request(store.index('clientPartyId').getAll(clientPartyId)) : await request(store.getAll());
    await transactionDone(transaction);
    return values as PhotoRecord[];
  }

  async addPhoto(input: Omit<PhotoRecord, 'id'|'createdAt'>): Promise<PhotoRecord> {
    if (!this.writer) throw new Error('READ_ONLY_TAB');
    if (!input.mimeType.startsWith('image/') || input.size > 10 * 1024 * 1024) throw new Error('PHOTO_INVALID');
    const photo: PhotoRecord = { ...input, id: crypto.randomUUID(), createdAt: new Date().toISOString() };
    const transaction = this.requireDb().transaction('blobs', 'readwrite'); transaction.objectStore('blobs').put(photo); await transactionDone(transaction);
    return photo;
  }

  async deletePhoto(id: string): Promise<void> {
    if (!this.writer) throw new Error('READ_ONLY_TAB');
    const transaction = this.requireDb().transaction('blobs', 'readwrite'); transaction.objectStore('blobs').delete(id); await transactionDone(transaction);
  }

  async getMeta<T>(key: string): Promise<T | null> {
    const transaction = this.requireDb().transaction('meta', 'readonly'), value = await request(transaction.objectStore('meta').get(key)) as LocalMeta | undefined;
    await transactionDone(transaction); return (value?.value as T | undefined) ?? null;
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    if (!this.writer) throw new Error('READ_ONLY_TAB');
    const transaction = this.requireDb().transaction('meta', 'readwrite'); transaction.objectStore('meta').put({ key, value }); await transactionDone(transaction);
  }

  async replaceFromRestore(snapshot: SalonSnapshot, photos: PhotoRecord[]): Promise<void> {
    if (!this.writer) throw new Error('READ_ONLY_TAB');
    if (!isBrowserSnapshot(snapshot)) throw new Error('RESTORE_SNAPSHOT_INVALID');
    const db = this.requireDb(), transaction = db.transaction(['state', 'blobs', 'operations'], 'readwrite');
    const current = await request(transaction.objectStore('state').get('active')) as StateRecord | undefined;
    const revision = (current?.revision ?? 0) + 1, at = new Date().toISOString();
    transaction.objectStore('state').put({ key: 'active', revision, updatedAt: at, snapshot: structuredClone(snapshot) } satisfies StateRecord);
    const blobStore = transaction.objectStore('blobs'); blobStore.clear(); photos.forEach((photo) => blobStore.put(photo));
    transaction.objectStore('operations').put({ id: `${revision}:restore`, revision, at, tabId: this.tabId, kind: 'restore' });
    await transactionDone(transaction); this.revision = revision;
  }

  close(): void {
    this.releaseLock?.(); this.releaseLock = null;
    if (this.fallbackLease !== null) clearInterval(this.fallbackLease);
    if (localStorage.getItem('calcomate.salon.writer')?.startsWith(this.tabId)) localStorage.removeItem('calcomate.salon.writer');
    this.db?.close(); this.db = null;
  }

  private requireDb(): IDBDatabase { if (!this.db) throw new Error('LOCAL_DATABASE_NOT_READY'); return this.db; }
  private async readRecord(): Promise<StateRecord | undefined> {
    const transaction = this.requireDb().transaction('state', 'readonly'), value = await request(transaction.objectStore('state').get('active')) as StateRecord | undefined;
    await transactionDone(transaction); return value;
  }
  private async writeInitial(snapshot: SalonSnapshot): Promise<void> {
    const transaction = this.requireDb().transaction('state', 'readwrite');
    transaction.objectStore('state').add({ key: 'active', revision: 1, updatedAt: new Date().toISOString(), snapshot } satisfies StateRecord);
    await transactionDone(transaction); this.revision = 1;
  }

  private async acquireWriterLock(): Promise<boolean> {
    if (navigator.locks?.request) {
      return new Promise<boolean>((resolve) => {
        void navigator.locks.request('calcomate-salon-single-writer', { ifAvailable: true }, async (lock) => {
          if (!lock) { resolve(false); return; }
          resolve(true);
          await new Promise<void>((release) => { this.releaseLock = release; });
        });
      });
    }
    const key = 'calcomate.salon.writer', now = Date.now(), current = localStorage.getItem(key), expires = Number(current?.split(':')[1] ?? 0);
    if (current && expires > now) return false;
    localStorage.setItem(key, `${this.tabId}:${now + 12000}`);
    if (!localStorage.getItem(key)?.startsWith(this.tabId)) return false;
    this.fallbackLease = window.setInterval(() => localStorage.setItem(key, `${this.tabId}:${Date.now() + 12000}`), 4000);
    return true;
  }
}

export async function deleteDatabase(name: string): Promise<void> {
  await new Promise<void>((resolve, reject) => { const deleting = indexedDB.deleteDatabase(name); deleting.onsuccess = () => resolve(); deleting.onerror = () => reject(deleting.error); deleting.onblocked = () => reject(new Error('STAGING_DATABASE_BLOCKED')); });
}
